import { FormattedMessage } from 'react-intl';
import { toast } from '@/hooks/useToast.tsx';
import { DeleteConfirmationForm } from './DeleteConfirmationForm.tsx';
import { useDeleteUserProfileByID, UserDTO } from '@/api/trafikportalen';
import { useIntl } from 'react-intl';
import { SubmitHandler } from 'react-hook-form';
import { z } from 'zod';
import { useNavigate } from '@tanstack/react-router';
import { useTenantID } from '@/hooks/useTenantID.tsx';
import { useCallback } from 'react';
import { checkUserPermissions } from './Auth/checkUserPermissions.ts';
import { useAuth } from '@/stores/auth.ts';

interface Props {
  administrator: UserDTO;
}

const deleteAdministratorFormSchema = z.object({
  id: z.string().uuid(),
});

type AdministratorDeleteType = z.infer<typeof deleteAdministratorFormSchema>;

export const DeleteSystemAdministratorForm = ({ administrator }: Props) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const tenantId = useTenantID();
  const deleteAdministrator = useDeleteUserProfileByID();
  const currentUserProfile = useAuth((state) => state.userProfile);

  const isDeletingSelf = administrator.email === currentUserProfile?.email;

  const onDelete: SubmitHandler<AdministratorDeleteType> = useCallback(
    async (data) => {
      await deleteAdministrator.mutateAsync(
        { ...data },
        {
          onSuccess: async () => {
            toast.success(
              {
                title: formatMessage(
                  {
                    defaultMessage: `{name} raderades`,
                  },
                  {
                    name: `${administrator.firstname} ${administrator.lastname}`,
                  },
                ),
              },
              { autoClose: 3000 },
            );

            await navigate({
              to: `/$tenantId/system-administrators`,
              params: { tenantId },
            });
          },
          onError: (error) => {
            console.error('Error: ', error);
            toast.error(
              {
                title: 'Ett fel inträffade',
                description:
                  'Försök spara igen senare, eller vänligen kontakta support.',
              },
              { autoClose: 3000 },
            );
          },
        },
      );
    },
    [
      deleteAdministrator,
      formatMessage,
      administrator.firstname,
      administrator.lastname,
      navigate,
      tenantId,
    ],
  );

  return (
    <DeleteConfirmationForm
      title={formatMessage({ defaultMessage: 'Radera' })}
      warningMessage={
        isDeletingSelf ? (
          <FormattedMessage defaultMessage="Du kan inte ta bort dig själv." />
        ) : (
          <FormattedMessage
            defaultMessage="All data för {name} kommer att raderas och kan inte ångras. Det kan ta upp till 10 minuter innan användaren blir utloggad och inte längre kan logga in."
            values={{
              name: `${administrator.firstname} ${administrator.lastname}`,
            }}
          />
        )
      }
      canDelete={
        !isDeletingSelf && checkUserPermissions({ permission: 'tenant:editor' })
      }
      onDelete={() => {
        onDelete({ id: administrator.user_profiles[0].id });
      }}
    />
  );
};
